/**
 * P12: Notes — built-in iPhone-style notepad.
 *
 * Left column = note list (pinned first, then by updated_at desc).
 * Right column = editor with title + body, auto-save on blur + explicit save.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../lib/api';
import CornerPanel from '../components/CornerPanel';
import { Plus, Pin, PinOff, Trash2, Save, Search } from 'lucide-react';
import { cstDateTime } from '../lib/time';
import { toast } from 'sonner';

export default function Notes() {
  const [notes, setNotes] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [dirty, setDirty] = useState(false);
  const [filter, setFilter] = useState('');
  const saveTimer = useRef(null);

  const selected = useMemo(
    () => notes.find((n) => n.id === selectedId) || null,
    [notes, selectedId]
  );

  const load = async () => {
    try {
      const { data } = await api.get(`/notes?_t=${Date.now()}`);
      setNotes(data.notes || []);
      if (!selectedId && (data.notes || []).length) {
        const first = data.notes[0];
        setSelectedId(first.id);
        setTitle(first.title);
        setBody(first.body);
      }
    } catch {
      toast.error('Failed to load notes');
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);   // initial

  // When user clicks a different note, flush any pending autosave for the
  // OUTGOING note before loading the new one (prevents losing in-flight
  // edits when the 1.2s debounce hasn't fired yet).
  const prevSelectedRef = useRef(null);
  useEffect(() => {
    const flushAndSwap = async () => {
      if (prevSelectedRef.current && dirty) {
        // Cancel pending debounced save; force a direct save of OLD note.
        if (saveTimer.current) clearTimeout(saveTimer.current);
        try {
          await api.put(`/notes/${prevSelectedRef.current}`, { title, body });
        } catch {}
      }
      prevSelectedRef.current = selectedId;
      if (selected) {
        setTitle(selected.title);
        setBody(selected.body);
        setDirty(false);
      }
    };
    flushAndSwap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // Flush on unmount / tab close — catches nav-away mid-edit.
  useEffect(() => {
    const beforeUnload = () => {
      if (dirty && selected && navigator.sendBeacon) {
        try {
          const blob = new Blob([JSON.stringify({ title, body })],
            { type: 'application/json' });
          const token = sessionStorage.getItem('atde_token');
          navigator.sendBeacon(
            `${process.env.REACT_APP_BACKEND_URL}/api/notes/${selected.id}?beacon=1&token=${token || ''}`,
            blob
          );
        } catch {}
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
      // Flush on unmount (route change)
      if (dirty && selected) {
        api.put(`/notes/${selected.id}`, { title, body }).catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, selected, title, body]);

  const create = async () => {
    try {
      const { data } = await api.post('/notes', { title: 'Untitled', body: '' });
      await load();
      setSelectedId(data.id);
      setTitle(data.title);
      setBody(data.body);
      setDirty(false);
      toast.success('Note created');
    } catch { toast.error('Create failed'); }
  };

  const save = async () => {
    if (!selected) return;
    try {
      await api.put(`/notes/${selected.id}`, { title, body });
      setDirty(false);
      await load();
    } catch { toast.error('Save failed'); }
  };

  const del = async () => {
    if (!selected) return;
    if (!window.confirm(`Delete "${selected.title}"?\n\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/notes/${selected.id}`);
      setSelectedId(null); setTitle(''); setBody('');
      await load();
      toast.success('Note deleted');
    } catch { toast.error('Delete failed'); }
  };

  const togglePin = async () => {
    if (!selected) return;
    try {
      await api.put(`/notes/${selected.id}`, { pinned: !selected.pinned });
      await load();
    } catch { toast.error('Pin toggle failed'); }
  };

  // Debounced autosave (1.2s after user stops typing)
  useEffect(() => {
    if (!dirty || !selected) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(save, 1200);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [title, body, dirty]);   // eslint-disable-line

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return notes;
    return notes.filter((n) => (n.title + ' ' + n.body).toLowerCase().includes(q));
  }, [notes, filter]);

  return (
    <div className="p-4">
      <CornerPanel
        title={`Notepad // ${notes.length} note${notes.length === 1 ? '' : 's'}`}
        right={
          <div className="flex items-center gap-2">
            {dirty && <span className="text-[10px] uppercase tracking-wider text-hold">● unsaved</span>}
            <button
              data-testid="note-save"
              onClick={save}
              disabled={!selected || !dirty}
              className="flex items-center gap-1 text-[11px] font-display tracking-[0.2em] uppercase border border-buy text-buy hover:bg-buy hover:text-black px-2 py-1 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Save size={12} /> save
            </button>
            <button
              data-testid="note-new"
              onClick={create}
              className="flex items-center gap-1 text-[11px] font-display tracking-[0.2em] uppercase border border-cyan-sys text-cyan-sys hover:bg-cyan-sys hover:text-black px-2 py-1"
            >
              <Plus size={12} /> new
            </button>
          </div>
        }
        testId="notes-panel"
      >
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-3 min-h-[520px]">
          {/* LIST */}
          <div className="border border-[var(--border-default)] bg-black p-2 space-y-1">
            <div className="flex items-center border border-[var(--border-muted)] px-2 mb-2">
              <Search size={12} className="text-cyan-sys mr-1" />
              <input
                data-testid="note-filter"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="FILTER..."
                className="flex-1 bg-transparent outline-none py-1 font-mono text-[11px] text-cyan-sys"
              />
            </div>
            {filtered.length === 0 && (
              <div className="text-[var(--txt-muted)] text-[11px] text-center py-6">// no notes</div>
            )}
            {filtered.map((n) => (
              <button
                key={n.id}
                onClick={() => setSelectedId(n.id)}
                data-testid={`note-item-${n.id}`}
                className={`block w-full text-left px-2 py-2 border ${
                  n.id === selectedId
                    ? 'border-cyan-sys bg-[rgba(8,247,254,0.06)]'
                    : 'border-transparent hover:border-[var(--border-muted)]'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    {n.pinned && <Pin size={10} className="text-hold shrink-0" />}
                    <span className="font-display tracking-wider text-[12px] truncate">{n.title || 'Untitled'}</span>
                  </div>
                </div>
                <div className="text-[10px] text-[var(--txt-muted)] truncate mt-0.5 font-mono">
                  {(n.body || '').replace(/\n/g, ' ').slice(0, 60) || '—'}
                </div>
                <div className="text-[9px] text-[var(--txt-secondary)] tracking-wider mt-0.5">
                  {cstDateTime(n.updated_at).slice(11)}
                </div>
              </button>
            ))}
          </div>

          {/* EDITOR */}
          <div className="border border-[var(--border-default)] bg-black p-3 flex flex-col">
            {!selected ? (
              <div className="flex-1 flex items-center justify-center text-[var(--txt-muted)] text-[12px]">
                // select a note or create a new one
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <input
                    data-testid="note-title-input"
                    value={title}
                    onChange={(e) => { setTitle(e.target.value); setDirty(true); }}
                    className="flex-1 bg-transparent border-b border-[var(--border-muted)] focus:border-cyan-sys outline-none py-1 font-display tracking-[0.15em] text-lg text-cyan-sys"
                    placeholder="TITLE"
                  />
                  <button
                    data-testid="note-pin"
                    onClick={togglePin}
                    title={selected.pinned ? 'Unpin' : 'Pin to top'}
                    className="text-[var(--txt-secondary)] hover:text-hold p-1"
                  >
                    {selected.pinned ? <PinOff size={14} /> : <Pin size={14} />}
                  </button>
                  <button
                    data-testid="note-delete"
                    onClick={del}
                    title="Delete note"
                    className="text-[var(--txt-secondary)] hover:text-sell p-1"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <textarea
                  data-testid="note-body-input"
                  value={body}
                  onChange={(e) => { setBody(e.target.value); setDirty(true); }}
                  placeholder="// start writing..."
                  className="flex-1 bg-transparent outline-none font-mono text-[13px] leading-[1.7] resize-none text-[var(--txt-primary)] placeholder:text-[var(--txt-muted)] min-h-[400px]"
                />
                <div className="text-[9px] text-[var(--txt-muted)] tracking-wider mt-2 text-right">
                  updated {cstDateTime(selected.updated_at)}
                </div>
              </>
            )}
          </div>
        </div>
      </CornerPanel>
    </div>
  );
}
